/**
 * SignpostingLocationPrompt
 *
 * Inline card requesting browser location permission.
 * No persistence — just gets current location and passes it back.
 * The system prompt already has the user's city from IP for
 * general searches; this is for precise nearby results.
 */

'use client'

import { useState, useCallback, useEffect } from 'react'
import { MapPin, Loader2, Check } from 'lucide-react'

interface LocationResult {
  latitude: number
  longitude: number
}

interface SignpostingLocationPromptProps {
  reason: string
  onLocationReceived: (location: LocationResult) => void
  onDeclined: () => void
}

type PromptState = 'idle' | 'loading' | 'success' | 'error'

// --------------

export function SignpostingLocationPrompt({
  reason,
  onLocationReceived,
  onDeclined,
}: SignpostingLocationPromptProps) {
  const [state, setState] = useState<PromptState>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (state === 'success') {
      const timer = setTimeout(() => setDismissed(true), 1500)
      return () => clearTimeout(timer)
    }
  }, [state])

  // --------------

  const handleShare = useCallback(() => {
    if (!navigator.geolocation) {
      setState('error')
      setErrorMessage('Geolocation is not supported by your browser')
      return
    }

    setState('loading')

    navigator.geolocation.getCurrentPosition(
      (position) => {
        onLocationReceived({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        })
        setState('success')
      },
      (error) => {
        setState('error')
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setErrorMessage(
              'Location permission denied. Please enable it in your browser settings.'
            )
            break
          case error.POSITION_UNAVAILABLE:
            setErrorMessage('Unable to determine your location.')
            break
          case error.TIMEOUT:
            setErrorMessage('Location request timed out.')
            break
          default:
            setErrorMessage('An error occurred getting your location.')
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    )
  }, [onLocationReceived])

  // --------------

  if (dismissed) return null

  if (state === 'success') {
    return (
      <div className="border-border/40 bg-card/50 max-w-sm rounded-xl border p-4 backdrop-blur-sm">
        <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
          <Check className="size-4" />
          <span className="text-sm font-medium">Location received</span>
        </div>
      </div>
    )
  }

  return (
    <div className="border-border/40 bg-card/50 max-w-sm rounded-xl border p-4 backdrop-blur-sm">
      <div className="flex items-center gap-2">
        <MapPin className="text-primary size-4" />
        <span className="text-sm font-medium">Location Request</span>
      </div>

      <p className="text-muted-foreground mt-2 text-sm">{reason}</p>

      <p className="text-muted-foreground/70 mt-2 text-[11px]">
        Your location is only used for this search.
      </p>

      {state === 'loading' && (
        <div className="text-muted-foreground mt-3 flex items-center gap-2">
          <Loader2 className="size-3.5 animate-spin" />
          <span className="text-xs">Getting your location...</span>
        </div>
      )}

      {state === 'error' && (
        <div className="mt-3">
          <p className="text-xs text-red-500">{errorMessage}</p>
          <button
            onClick={handleShare}
            className="text-primary mt-1 text-xs underline"
          >
            Try again
          </button>
        </div>
      )}

      {(state === 'idle' || state === 'error') && (
        <div className="mt-3 flex gap-2">
          <button
            onClick={handleShare}
            className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90"
          >
            Share Location
          </button>
          <button
            onClick={onDeclined}
            className="bg-muted text-muted-foreground rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-80"
          >
            Decline
          </button>
        </div>
      )}
    </div>
  )
}
